import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Role } from 'enums/role';
import { CognitoAuthUser } from 'hooks/pages/useSignIn.types';
import type { RootState } from 'store';

import { UserProps } from './user.types';

const initialState: UserProps = {
  _id: '',
  accessToken: '',
  cognitoUsername: '',
  displayName: '',
  elationId: '',
  elationPracticeId: '',
  email: '',
  firstName: '',
  idToken: '',
  isEmailVerified: false,
  isFederated: false,
  lastLogin: '',
  lastName: '',
  profileImage: '',
  rating: undefined,
  refreshToken: '',
  status: '',
  timezone: '',
  userRoleInfo: {
    _id: '',
    color: '',
    editingPermissions: [''],
    name: '',
    pagePermissions: [''],
    shortName: 'MD'
  },
  userType: {
    _id: '',
    name: '',
    shortCode: ''
  }
};

const userSlice = createSlice({
  initialState,
  name: 'user',
  reducers: {
    clearUser: (): UserProps => {
      return initialState;
    },
    setToken: (
      state,
      { payload: { accessToken, idToken } }: PayloadAction<{ accessToken: string; idToken: string }>
    ) => {
      state.accessToken = accessToken;
      state.idToken = idToken;
    },
    setUser: (
      state,
      action: PayloadAction<UserProps | (CognitoAuthUser & UserProps & { isFederated: boolean })>
    ) => {
      if ('session' in action.payload) {
        const session = (action.payload as CognitoAuthUser)?.session;

        const user: { [i: string]: unknown } = {};
        if (session?.accessToken && session?.idToken) {
          const idPayload = session.idToken.payload;
          // MongoDB user _id ref
          user._id = action.payload._id;
          user.isFederated = action.payload.isFederated;
          user.cognitoUsername = idPayload['cognito:username'];
          user.accessToken = session?.accessToken?.toString();
          user.idToken = session?.idToken?.toString();
          user.elationId = action.payload.elationId;
          user.elationPracticeId = action.payload.elationPracticeId;
          user.isEmailVerified = idPayload.email_verified;
          user.lastLogin = action.payload.lastLogin;
          user.status = action.payload.status;
          user.firstName = idPayload.given_name || action.payload.firstName;
          user.lastName = idPayload.family_name || action.payload.lastName;
          user.displayName = idPayload.displayName || action.payload.displayName;
          user.profileImage = idPayload.picture || action.payload.profileImage;
          user.email = idPayload.email;
          user.timezone = idPayload.zoneinfo || action.payload.timezone;
          user.ai = action.payload.ai;

          const userRoleInfo = action.payload.userRoleInfo;
          if (userRoleInfo) {
            user.userRoleInfo = {
              _id: userRoleInfo._id,
              color: userRoleInfo.color,
              editingPermissions: userRoleInfo.editingPermissions,
              name: userRoleInfo.name,
              pagePermissions: userRoleInfo.pagePermissions,
              shortName: userRoleInfo.shortName
            };
          }
          const userType = action.payload.userType;
          if (userType) {
            user.userType = {
              _id: userType._id,
              name: userType.name,
              shortCode: userType.shortCode
            };
          }
          user.rating = action.payload.rating;
        }

        return {
          ...state,
          ...user
        };
      } else
        return {
          ...state,
          ...action.payload
        };
    },
    updateAIMessageTaskAutoRoutingRead: (
      state,
      action: PayloadAction<{ messageTaskAutoRoutingRead: string }>
    ) => {
      return {
        ...state,
        ai: { messageTaskAutoRoutingRead: action.payload.messageTaskAutoRoutingRead }
      };
    },
    updateUserTimezone: (state, action: PayloadAction<{ timezone: string }>) => {
      return {
        ...state,
        timezone: action.payload.timezone
      };
    }
  }
});

export const selectUser = (state: RootState) => state.user;

export const selectCanCreateFrontDeskChannel = createSelector(
  (state: RootState) => state.user.userRoleInfo.editingPermissions,
  (editingPermissions) => editingPermissions?.includes('CREATE_FRONT_DESK_CHANNEL')
);

// Insurance permissions
export const selectCanViewInsuranceCoverage = createSelector(
  (state: RootState) => state.user.userRoleInfo.pagePermissions,
  (pagePermissions) => pagePermissions?.includes('INSURANCE_COVERAGE_VIEW')
);
export const selectCanEditInsuranceCoverage = createSelector(
  (state: RootState) => state.user.userRoleInfo.editingPermissions,
  (editingPermissions) => editingPermissions?.includes('INSURANCE_COVERAGE_EDIT')
);
export const selectCanViewBridgeHealth = createSelector(
  (state: RootState) => state.user.userRoleInfo.pagePermissions,
  (pagePermissions) => pagePermissions?.includes('INSURANCE_BRIDGE_VIEW')
);
export const selectCanEditBridgeHealth = createSelector(
  (state: RootState) => state.user.userRoleInfo.editingPermissions,
  (editingPermissions) => editingPermissions?.includes('INSURANCE_BRIDGE_EDIT')
);
export const selectCanViewPatientEligibility = createSelector(
  (state: RootState) => state.user.userRoleInfo.pagePermissions,
  (pagePermissions) => pagePermissions?.includes('INSURANCE_ELIGIBILITY_VIEW')
);

// Granted to CS and AD via migrations (see lifemd/migrations/data/
// 20260501000000-add-invoice-cancel-permission.js and
// 20260501120000-add-invoice-cancel-permission-admin-roles.js).
export const selectCanCancelInvoice = createSelector(
  (state: RootState) => state.user.userRoleInfo.editingPermissions,
  (editingPermissions) => editingPermissions?.includes('INVOICE_CANCEL')
);

// Admin gate for workflow retry/cancel/skip/undo actions.
export const selectIsAdmin = createSelector(
  (state: RootState) => state.user.userType?.name,
  (userTypeName) => userTypeName === Role.AD
);

// Fine-grained permission that gates editing an activity input before a retry.
export const selectCanEditWorkflowInput = createSelector(
  (state: RootState) => state.user.userRoleInfo.editingPermissions,
  (editingPermissions) => editingPermissions?.includes('WORKFLOW_INPUT_EDIT')
);

// Signable documents admin page: view gates the list and editor, edit gates save/archive/restore.
export const selectCanViewSignableDocuments = createSelector(
  (state: RootState) => state.user.userRoleInfo.pagePermissions,
  (pagePermissions) => !!pagePermissions?.includes('SIGNABLE_DOCUMENTS_VIEW')
);
export const selectCanEditSignableDocuments = createSelector(
  (state: RootState) => state.user.userRoleInfo.editingPermissions,
  (editingPermissions) => !!editingPermissions?.includes('SIGNABLE_DOCUMENTS_EDIT')
);

export const {
  clearUser,
  setToken,
  setUser,
  updateAIMessageTaskAutoRoutingRead,
  updateUserTimezone
} = userSlice.actions;

export default userSlice.reducer;
